import type { IUpdateArticleStockCmd } from "@/data/commands/catalog/UpdateArticleStockCmd";
import { Factory } from "@/data/dao/Factory";
import { CatalogDao } from "@/data/dao/service/CatalogDao";
import type { IStockTableViewArticle } from "@/data/model/Article";

import { NumberInput } from "@/portal/formControls/NumberInput";
import { Switch } from "@/portal/formControls/Switch";
import { BaseEntityForm } from "@/portal/forms/BaseEntityForm";

export class ArticleStockSettingsForm extends BaseEntityForm<IStockTableViewArticle, IUpdateArticleStockCmd> {
    private activeSwitch: Switch;
    private remainingInput: NumberInput;
    private securityStockInput: NumberInput;
    private thresholdInput: NumberInput;

    constructor(element: JQuery) {
        super(element, {
            getSingle: undefined as any,
            create: undefined as any,
            getData: undefined as any,
            remove: undefined as any,
            update: (i, d) => Factory.getInstance<CatalogDao>(CatalogDao).setArticleStock(i, d),
        });

        this.activeSwitch = new Switch(this.$form.find('input[name="isactive"]'));
        this.remainingInput = new NumberInput(this.$form.find('.form-control[name="remaining"]'), {
            min: 0,
            step: 1,
        });

        this.remainingInput.onChange = () => this.showHideDeactivationHint();

        this.securityStockInput = new NumberInput(this.$form.find('.form-control[name="securitystock"]'), {
            min: 0,
            step: 1,
        });

        this.securityStockInput.onChange = () => this.showHideDeactivationHint();

        this.thresholdInput = new NumberInput(this.$form.find('.form-control[name="threshold"]'), {
            min: 0,
            step: 1,
        });

        this.activeSwitch.onChange = () => this.showHideActivatedFields();
    }

    public setFormData(model?: IStockTableViewArticle) {
        const stock = model === undefined ? undefined : model.stock;

        this.activeSwitch.setValue(stock === undefined ? false : true);
        this.remainingInput.setValue(stock === undefined ? 1 : stock.remaining);
        this.thresholdInput.setValue(
            stock === undefined || stock.notifyThreshold === undefined ? "" : stock.notifyThreshold
        );
        this.securityStockInput.setValue(
            stock === undefined || stock.safetyStock === undefined ? "" : stock.safetyStock
        );

        this.showHideActivatedFields();
        this.showHideDeactivationHint();
    }

    private showHideActivatedFields() {
        if (this.activeSwitch.getValue()) this.$form.find("#remaining, #securitystock, #threshold").show();
        else this.$form.find("#remaining, #securitystock, #threshold").hide();
    }

    private showHideDeactivationHint() {
        let securityStock = this.securityStockInput.getValue();
        if (securityStock === undefined || securityStock == null) securityStock = 0;

        if (this.remainingInput.getValue()! <= securityStock) {
            this.$form.find("#remaining span.text-danger").show();
        } else {
            this.$form.find("#remaining span.text-danger").hide();
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public getFormData($form: JQuery): IUpdateArticleStockCmd {
        const isActive = this.activeSwitch.getValue();

        const data: IUpdateArticleStockCmd = {
            isActive: isActive,
            remaining: isActive ? this.remainingInput.getValue() : undefined,
            safetyStock: isActive ? this.securityStockInput.getValue() : undefined,
            notifyThreshold: isActive ? this.thresholdInput.getValue() : undefined,
        };

        let hasError = false;
        this.$form.find(".is-invalid").removeClass("is-invalid");

        if (isActive) {
            if (data.remaining === undefined || isNaN(data.remaining) || data.remaining < 0) {
                this.remainingInput.setError(true);
                hasError = true;
            }

            if (data.safetyStock !== undefined && (isNaN(data.safetyStock) || data.safetyStock < 0)) {
                this.securityStockInput.setError(true);
                hasError = true;
            }

            if (data.notifyThreshold !== undefined && (isNaN(data.notifyThreshold) || data.notifyThreshold < 0)) {
                this.thresholdInput.setError(true);
                hasError = true;
            }
        }

        return (hasError ? undefined : data) as any;
    }
}
